import React, { Component } from "react";
import classNames from "classnames";
import Infinite from "react-infinite";
import { withContentRect } from "react-measure";
import selectn from "selectn";
import shortid from "shortid";

import Spinner from "../../Library/Spinner";
import StoryCard from "../../Library/StoryCard";
import SummaryCard from "../../Library/SummaryCard";
import EventCard from "../../Library/EventCard";
import Advert from "../../Library/Advert";

import styles from "./StoriesStream.css";

const MeasuredStory = withContentRect("client")(({ measureRef, Wrapper, story, storyClassName, key, showDivider, index, isLastStory, adId, stream }) => (
  <div ref={measureRef} key={key} role="article" tabIndex="-1">
    {showDivider === "start" && <hr className={classNames("mx-4", styles.separator)} aria-hidden={true}/>}
    {
      story.isAd ?
        <Advert id={shortid.generate()} advert={story} adId={adId} stream={stream}/> :
        <Wrapper story={story} isLastStory={isLastStory} className={classNames(index !== 0 ? "my-3" : "mb-3", storyClassName)}/>
    }
    {showDivider === "end" && <hr className={classNames("mx-4", styles.separator)} aria-hidden={true}/>}
  </div>
));

class StoriesStream extends Component {
  constructor(props) {
    super(props);
    this._handleInfiniteLoad = this._handleInfiniteLoad.bind(this);
    this.state = {
      loading: false,
      page: 1,
      streamResults: [],
    };
  }

  componentDidMount() {
    if (this.props.stories.isFinished) {
      this.setElementHeights(this.props.stories);
    }
  }

  componentWillReceiveProps(nextProps) {
    const nextStories = nextProps.stories;
    const currentStories = this.props.stories;
    if (nextStories.isFinished && nextStories.results && nextStories.results !== currentStories.results) {
      this.setElementHeights(nextStories);
    }
    if (!currentStories.isError && nextStories.isError) {
      this.setState({ edgeOffset: undefined, loading: false });
    }
    if (!nextStories.results || nextStories.isLoading) {
      this.setState({ page: 1 });
    }
  }

  getStream = (stream) => {
    if (!stream) return "misc";
    if (stream === "top") return "homepage";
    return `section-${stream}`;
  };

  setElementHeights(stories) {
    let results = this.getStoriesAndAds(stories);
    // Because children haven't been measured yet, set height initially to an approximate value: 500px
    const edgeOffset = this.props.isSummary || this.props.isEvent ? 200 : 500;
    let elemHeights = results.reduce((accum, story) => {
      return { ...accum, [story.id]: selectn(`elementHeights.${story.id}`, this.state) || edgeOffset };
    }, {});
    this.setState({
      // Keep heights that have already been calculated
      elementHeights: elemHeights,
      edgeOffset,
      streamResults: results,
    });
  }

  getStoriesAndAds = (stories) => {
    if (!this.props.showAds) return stories.results;

    let results = [ ...stories.results ];
    let placedAds = 0;
    let startAd = 2;
    const gap = 4;

    // First ad after 1st post for top
    if (this.props.stream === "top") {
      results.splice(1, 0, this.getAd(1, results[0], results[1]));
      startAd = 7;
    }

    if (results.length < startAd) return results;

    let insertIndex = startAd + placedAds + (placedAds * gap);

    // Ads every 4 posts starting after 6th for top and 2nd for other streams
    while(insertIndex <= results.length) {
      results.splice(insertIndex, 0, this.getAd(placedAds + 2, results[insertIndex - 1], results[insertIndex]));
      placedAds += 1;
      insertIndex = startAd + placedAds + (placedAds * gap);
    }

    return results;
  };

  getAd = (index, beforePost, afterPost) => {
    const postIds = [selectn("id", beforePost), selectn("id", afterPost)];
    const beforeTags = beforePost ? (beforePost.tags || []).map((t) => t.name).concat(selectn("primary_tag.name", beforePost)) : [];
    const afterTags = afterPost ? (afterPost.tags || []).map((t) => t.name).concat(selectn("primary_tag.name", afterPost)) : [];

    return {
      id: `ad_${index}`,
      isAd: true,
      index,
      postIds,
      beforeTags,
      afterTags,
    };
  };

  _updateSize(id, height) {
    if (this.state.elementHeights[id] !== height) {
      this.setState({
        elementHeights: {
          ...this.state.elementHeights,
          [id]: height,
        },
      });
    }
  }

  _handleInfiniteLoad() {
    const page = this.state.page + 1;
    this.setState({ loading: true, page });
    this.props.next(page).then(() => this.setState({ loading: false }));
  }

  render() {
    const { stories, storyClassName, isSummary, isEvent, showUnread } = this.props;
    if (!stories.results || stories.isLoading) return (
      <div className={styles.spinnerWrapper}><Spinner /></div>
    );

    const Wrapper = isSummary ? SummaryCard : isEvent ? EventCard : StoryCard;

    if (this.state.elementHeights) {
      // We cannot guarantee the uniqueness of IDs in elementHeights so we need
      // to map them back against streamResults to return an array of element heights.
      // When ads don't render assign height of 0 or infinite scroll breaks
      const elementHeights = this.state.streamResults.map((story) => this.state.elementHeights[story.id] || (story.isAd ? 0 : 500));

      return (
        <Infinite
          useWindowAsScrollContainer
          elementHeight={elementHeights}
          infiniteLoadBeginEdgeOffset={this.state.edgeOffset}
          preloadBatchSize={Infinite.containerHeightScaleFactor(2)}
          preloadAdditionalHeight={Infinite.containerHeightScaleFactor(30)}
          loadingSpinnerDelegate={<Spinner />}
          onInfiniteLoad={this._handleInfiniteLoad}
          isInfiniteLoading={this.state.loading}
        >
          {this.state.streamResults.map((story, index) => {
            let showDivider = this.props.lastSeenId === story.id && index !== 0 ? "start" :
              !this.props.lastSeenId && index === (stories.results.length - 1) ? "end" : false;
            return <MeasuredStory
              key={story.id}
              index={index}
              adId={this.props.adId}
              stream={this.getStream(this.props.stream)}
              isLastStory={stories.results.length - 1 === index}
              story={story}
              showDivider={showUnread && showDivider}
              Wrapper={Wrapper}
              storyClassName={storyClassName}
              onResize={(contentRect) => {
                this._updateSize(story.id, contentRect.client.height);
              }}
            />;
          })}
        </Infinite>
      );
    } else {
      return null;
    }
  }
}

export default StoriesStream;



// WEBPACK FOOTER //
// ./src/components/Library/StoriesStream/StoriesStream.js